import { Box, Button, AppBar, Toolbar, Typography } from '@mui/material'
import { GLOBAL_PROFILE } from 'components/Root.loader'
import OverflowText from 'components/ui/OverflowText'
import StatusBadge from 'components/ui/StatusBadge'
import { Document } from 'entities/Documents'
import { useConfirm } from 'hooks/useDialog'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'

interface Props {
  docDetail: Document
  onSave: () => void
  onSend: () => void
  onDownload: () => void
  setOpenDelDialog: (x: boolean) => void
  reload: () => void
}

function VerifyHeader({ docDetail, onSave, onSend, onDownload, setOpenDelDialog, reload }: Props) {
  const navigate = useNavigate()
  const confirm = useConfirm()
  const location = useLocation()
  const { handleSubmit } = useFormContext()

  const { documentName } = docDetail.docInfo

  // btn display config
  const { isManager, isAdmin } = GLOBAL_PROFILE
  const { isNew, isChecking, documentStatusNo } = docDetail
  const showCheck = (isNew || isChecking) && !isManager && !isAdmin
  const showConfirm = docDetail.canConfirm()
  const showSend = docDetail.canSend()

  const back = () => {
    if (location?.state?.fromIndexPage) {
      navigate(-1)
    } else {
      navigate('../home')
    }
  }

  const openDelDocDialog = () => {
    setOpenDelDialog(true)
  }

  const confirmDoc = handleSubmit(async (data) => {
    try {
      await docDetail.confirm(data)
      reload()
    } catch (e) {
      console.log('verify data 更新失敗')
    }
  })

  const handleCheck = () => {
    confirm({
      message:
        '結果チェックを完了しますか？完了後は編集不可となりベリファイチェックが実行されます。',
      actionText: 'はい',
      cancelText: 'いいえ',
      onConfirm: confirmDoc,
    })
  }

  const verifyDoc = handleSubmit(async (data) => {
    try {
      await docDetail.verify(data)
      reload()
    } catch (e) {
      console.log('verify data 更新失敗')
    }
  })

  const handleVerify = () => {
    confirm({
      message: 'ベリファイチェックを完了しますか？完了後は結果出力が可能となります。',
      actionText: 'はい',
      cancelText: 'いいえ',
      onConfirm: verifyDoc,
    })
  }

  return (
    <AppBar position="absolute" sx={{ color: 'text.primary', backgroundColor: 'common.white' }}>
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginRight: 'auto',
          }}
        >
          <StatusBadge status={documentStatusNo} sx={{ mr: 2 }} />
          <Typography
            variant="h2"
            style={{
              fontWeight: 'normal',
              fontSize: '1rem',
              maxWidth: '40vw',
            }}
          >
            <OverflowText>{documentName}</OverflowText>
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, ml: 2 }}>
          <Button onClick={back} variant="outlined" color="secondary" data-testid="back-button">
            戻る
          </Button>

          {docDetail.canSave() && (
            <Button color="primary" variant="outlined" onClick={onSave} data-testid="save-button">
              一時保存
            </Button>
          )}
          {docDetail.canCsvDownloadable() && (
            <Button
              disableElevation
              color="primary"
              variant="contained"
              onClick={() => onDownload()}
              data-testid="download-results-button"
            >
              {docDetail.canSave() && '一時保存&'}結果のダウンロード
            </Button>
          )}
          {showCheck && (
            <Button
              disableElevation
              variant="contained"
              disabled={!docDetail.canCheck()}
              sx={{
                color: 'black',
                backgroundColor: 'warning.light',
                '&:hover': {
                  backgroundColor: 'warning.main',
                },
              }}
              onClick={handleCheck}
              data-testid="check-button"
            >
              チェック済み
            </Button>
          )}
          {showConfirm && (
            <Button
              disableElevation
              variant="contained"
              disabled={!docDetail.canConfirm()}
              sx={{
                color: 'white',
                backgroundColor: 'info.light',
                '&:hover': {
                  backgroundColor: 'info.main',
                },
              }}
              onClick={handleVerify}
              data-testid="verify-button"
            >
              確認済み
            </Button>
          )}
          {showSend && (
            <Button
              disableElevation
              color="primary"
              variant="contained"
              disabled={!docDetail.canSend()}
              onClick={onSend}
              data-testid="send-results-button"
            >
              結果の送信
            </Button>
          )}

          <Button
            disableElevation
            variant="contained"
            color="secondary"
            onClick={openDelDocDialog}
            data-testid="delete-button"
          >
            削除
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default VerifyHeader
